import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import BankTransferService, { BankTransfer } from "../services/bank-transfer-service";

interface Props {
  transfer: BankTransfer | null;
  onClose: () => void;
  onDone: () => void;
}

const money = (v: number | null | undefined) => `${Number(v || 0).toLocaleString("vi-VN")} ₫`;

/** Gán tay 1 khoản tiền cho 1 đơn. Chuyển thiếu thì BE trả 409 → hỏi lại rồi mới gửi `allow_underpaid`. */
const BankTransferAssignModal: React.FC<Props> = ({ transfer, onClose, onDone }) => {
  const { fetch: assignOrder, loading } = BankTransferService.assignOrder();
  const [orderCode, setOrderCode] = useState("");

  useEffect(() => {
    // Đã bóc được mã đơn (chuyển thiếu / cần kiểm tra) → điền sẵn để kế toán chỉ việc xác nhận.
    setOrderCode(transfer?.order_code || "");
  }, [transfer]);

  if (!transfer) return null;

  const submit = async (allowUnderpaid = false) => {
    const code = orderCode.trim().toUpperCase();
    if (!code) return showToast({ type: "warning", message: "Vui lòng nhập mã đơn" });

    const res = await assignOrder(transfer.id, {
      order_code: code,
      allow_underpaid: allowUnderpaid || undefined,
    });
    if (res?.status === 200) {
      showToast({ type: "success", message: `Đã ghi nhận thanh toán cho đơn ${code}` });
      onDone();
      onClose();
      return;
    }
    if (res?.status === 409 && !allowUnderpaid) {
      const ok = window.confirm(
        `${res.message}.\n\nVẫn ghi nhận đơn ${code} là ĐÃ THANH TOÁN với khoản tiền này?`,
      );
      if (ok) await submit(true);
      return;
    }
    showToast({ type: "error", message: res?.message || "Gán đơn thất bại" });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[520px] max-w-[95vw] p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800">Gán giao dịch cho đơn hàng</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm space-y-1.5">
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Số tiền nhận</span>
            <span className="font-semibold text-slate-800">{money(transfer.amount)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-500 whitespace-nowrap">Nội dung CK</span>
            <span className="text-slate-700 text-right break-all">{transfer.content || "—"}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">Người chuyển</span>
            <span className="text-slate-700 text-right">{transfer.remitter_name || "—"}</span>
          </div>
          {transfer.note && (
            <div className="pt-1.5 border-t border-slate-200 text-slate-600">{transfer.note}</div>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm text-slate-600 mb-1">
            Mã đơn nhận khoản tiền này <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            autoFocus
            value={orderCode}
            onChange={(e) => setOrderCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) submit();
            }}
            placeholder="VD: OD1001"
            className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
          <p className="mt-1 text-xs text-slate-400">
            Đơn sẽ được ghi nhận đã thanh toán chuyển khoản, kèm phiếu thu tự động. Nhân viên giao vẫn cần bấm hoàn thành đơn.
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-slate-50">
            Hủy
          </button>
          <button
            disabled={loading}
            onClick={() => submit()}
            className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
          >
            {loading ? "Đang lưu..." : "Ghi nhận thanh toán"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BankTransferAssignModal;
